/**
 * Returns true when a value is missing (null, undefined, empty, or whitespace-only).
 * @param {*} value - Value to check.
 * @returns {boolean} Whether the value is blank.
 */
const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';

/**
 * Adapts purchase passengers to the shape the passenger validator reads.
 *
 * One concept travels under three names and nothing owns the translation between them:
 *
 * - `nationality` is the funnel's own form field. It holds a brand-specific code — CDS's
 *   `nationalities.json` maps `Peruano` to `'01'` — and it is what `utils/formValidations` reads.
 * - `iso_country_code` is what the funnel actually sends. `savePassengers` posts the form's
 *   `nationality` under that name, and `Passenger` stores it in a `string(3)` column.
 * - `nationality_id` is an integer column, `belongs_to :nationality, class_name: 'Country'`. A
 *   foreign key into a different table, in a different domain from `'01'`, and nothing derives it
 *   from what the buyer chose — `PurchaseFactory`'s exchange path stamps `Country.first.id`.
 *
 * `Api::V2::PassengerSerializer` returns the last two and never the first, so a passenger read off
 * the purchase always looks like it is missing a nationality. On a tenant with
 * `NATIONALITY_PER_PASSENGER` that is every passenger of every purchase, and two callers act on it:
 * Quick Purchase force-opens the passengers form (CON-1076) for a field the buyer cannot see is
 * missing — the form is prefilled from stored data and shows it filled — and the payment-attempt
 * audit reports it missing on every attempt, in the very telemetry CON-1076 added to size this
 * problem.
 *
 * `isoCountryCode` is preferred because it is the round trip of the form's own value: what the
 * funnel sent is what comes back. `nationalityId` stays as a fallback for passengers written by
 * some other path, where it at least answers the only question asked here — whether a nationality
 * was recorded at all.
 *
 * All of this is confined to reading purchase passengers. The validator keeps requiring
 * `nationality`, because form values carry these fields and mean different things by them:
 * `StaticPassengersForm`'s `cleanPassenger` nulls `nationality` while stamping `nationalityId: 1`,
 * so teaching the validator to take either would let a cleared passenger submit with no nationality
 * at all — on the same tenants this fix is for.
 *
 * @param {Array<object>} [passengers] - Passengers as the purchase holds them.
 * @returns {Array<object>} The same passengers, with `nationality` filled in from `isoCountryCode`,
 * or from `nationalityId` where that is all the purchase carries.
 */
export default function purchasePassengersForValidation(passengers = []) {
  if (!Array.isArray(passengers)) return [];

  return passengers.map((passenger) => {
    if (!passenger || !isBlank(passenger.nationality)) return passenger;

    const roundTripped = [passenger.isoCountryCode, passenger.nationalityId].find(
      (value) => !isBlank(value),
    );
    if (isBlank(roundTripped)) return passenger;

    return { ...passenger, nationality: roundTripped };
  });
}
